import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { type ChipOption } from '@/components/Chips/Chips';
import { DealCard } from '@/components/DealCard';
import { HeroCataloguePremium } from '@/components/HeroCataloguePremium/HeroCataloguePremium';
import { type LocationSuggestion } from '@/components/LocationSearchInput/LocationSearchInput';
import { SkeletonCard } from '@/components/Skeleton';
import { useToast } from '@/components/Toast';
import { useAdvisorDirectory } from '@/hooks/useAdvisorDirectory';
import { PROPERTY_TYPES, type PropertyType } from '@/lib/db/deals';
import { HOME_META } from '@/lib/seo/site';
import { useSeoHead } from '@/lib/seo/useSeoHead';
import {
  useCatalogueAutocomplete,
  useCatalogueDeals,
  useCatalogueLocationCorpus,
  useCatalogueStats,
  type CatalogueTypeFilter,
} from './DealsPage.data';
import { buildPaginationItems } from './DealsPage.pagination';
import {
  DEFAULT_FILTERS,
  readFiltersFromUrl,
  readPageFromUrl,
  writeFiltersToUrl,
  type Filters,
  type Sort,
} from './DealsPage.url';

const PER_PAGE = 9;
const URL_DEBOUNCE_MS = 300;
const LOCATION_SUGGESTIONS_CAP = 8;

const BEDROOMS_OPTS = ['studio', 'one', 'two', 'three', 'fourPlus'] as const;
type BedroomKey = (typeof BEDROOMS_OPTS)[number];

// "fourPlus" expands to bedrooms ≥ 4 — the deals.bedrooms CHECK constraint
// caps at 10 (migration 003), so the filter list covers the full range.
function expandBedroomKeys(keys: string[]): number[] {
  const out: number[] = [];
  for (const key of keys) {
    if (key === 'studio') out.push(0);
    else if (key === 'one') out.push(1);
    else if (key === 'two') out.push(2);
    else if (key === 'three') out.push(3);
    else if (key === 'fourPlus') out.push(4, 5, 6, 7, 8, 9, 10);
  }
  return out;
}

function formatRelativeTime(
  iso: string,
  locale: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const diffMin = Math.round(diffMs / 60_000);
  const diffHour = Math.round(diffMs / 3_600_000);
  const diffDay = Math.round(diffMs / 86_400_000);
  if (Math.abs(diffMin) < 60)
    return t('catalogue.metaUpdatedRelative.minutes', { count: Math.max(1, Math.abs(diffMin)) });
  if (Math.abs(diffHour) < 24)
    return t('catalogue.metaUpdatedRelative.hours', { count: Math.abs(diffHour) });
  if (Math.abs(diffDay) < 30)
    return t('catalogue.metaUpdatedRelative.days', { count: Math.abs(diffDay) });
  // Beyond a month, fall back to a localised absolute date — keeping the
  // header concise and avoiding "67 days ago" style ugliness.
  return new Date(iso).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function DealsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // `/` is the catalogue (ADR-018) — the home head lives here, not on the
  // retired HomePage.
  useSeoHead(HOME_META);

  // URL is the source of truth for navigable state. We hydrate `filters`
  // from it once on mount, then keep them in sync via the debounced effect
  // below. We don't re-read on every URL change to avoid clobbering the
  // user's in-flight typing.
  const [filters, setFilters] = useState<Filters>(() => readFiltersFromUrl(searchParams));

  const togglePropertyType = (pt: PropertyType) => {
    const next = filters.propertyType.includes(pt)
      ? filters.propertyType.filter((x) => x !== pt)
      : [...filters.propertyType, pt];
    setFilters({ ...filters, propertyType: next });
  };

  // Offset pagination (UI_Behavior §4.10 + UB7) — 0-based page index,
  // hydrated once on mount from `?page=` (deep linking, Spec L675), same
  // contract as `filters` above.
  const [page, setPage] = useState(() => readPageFromUrl(searchParams));

  // Debounce-write filters + page to URL. 300 ms is the Spec §M3 contract
  // for every filter change (incl. range drags), so back/forward stays
  // navigable instead of recording a dozen transitional states.
  useEffect(() => {
    const id = setTimeout(() => {
      const next = writeFiltersToUrl(filters, page + 1);
      setSearchParams(next, { replace: false });
    }, URL_DEBOUNCE_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  // Reset to the first page on any filter / sort change (the result set
  // changes, so the old offset points at arbitrary rows). Skip the very
  // first run — resetting there would clobber a deep-linked `?page=`.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(0);
  }, [filters]);

  // Wire all 7 filters + 4 sort options to the data layer. `bedrooms`
  // is expanded from UI keys ("studio"/"one"/.../"fourPlus") to the
  // explicit list of integer values the DB column holds.
  const expandedBedrooms = useMemo(() => expandBedroomKeys(filters.bedrooms), [filters.bedrooms]);

  const query = useCatalogueDeals({
    typeFilter: filters.typeFilter,
    priceMin: filters.priceMin,
    priceMax: filters.priceMax,
    areaMin: filters.areaMin,
    areaMax: filters.areaMax,
    zoneKey: filters.zoneKey,
    communityKey: filters.communityKey,
    buildingKey: filters.buildingKey,
    locationQuery: filters.locationText,
    discountMin: filters.discountMin,
    bedrooms: expandedBedrooms,
    propertyType: filters.propertyType as PropertyType[],
    searchQuery: filters.q,
    sortBy: filters.sortBy,
    page,
    pageSize: PER_PAGE,
  });

  useEffect(() => {
    if (query.status === 'error' && query.error) {
      toast.show({ type: 'error', message: query.error.message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.status, query.error]);

  const stats = useCatalogueStats({
    typeFilter: filters.typeFilter,
    priceMin: filters.priceMin,
    priceMax: filters.priceMax,
    areaMin: filters.areaMin,
    areaMax: filters.areaMax,
    zoneKey: filters.zoneKey,
    communityKey: filters.communityKey,
    buildingKey: filters.buildingKey,
    locationQuery: filters.locationText,
    discountMin: filters.discountMin,
    bedrooms: expandedBedrooms,
    propertyType: filters.propertyType as PropertyType[],
    searchQuery: filters.q,
  });

  // Location autocomplete corpus (addendum ADR-045 §8) — DISTINCT location text
  // values across the published catalogue, fetched once and filtered
  // client-side. Sourced from deal text, so NULL-key deals are included.
  const corpusQuery = useCatalogueLocationCorpus();

  // Advisor directory (M13) — fetched once so each card's WhatsApp CTA targets
  // the deal's advisor (assigned → default cascade) instead of the agency
  // number. `resolveWhatsAppPhone` returns undefined until advisors load (and
  // when none qualifies), which hides the card CTA rather than mis-dialing.
  const { resolveWhatsAppPhone } = useAdvisorDirectory();

  // Debounced search: SearchInput already debounces internally. We expose
  // the synchronous value for controlled-input updates and only commit the
  // debounced value to `filters` (which is what feeds the URL + query).
  const [searchDraft, setSearchDraft] = useState(filters.q);
  useEffect(() => setSearchDraft(filters.q), [filters.q]);

  const autocomplete = useCatalogueAutocomplete(searchDraft);

  const reset = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchDraft('');
    // Explicit — the filter-change effect skips when filters are already
    // default, which would otherwise leave a stale page offset behind.
    setPage(0);
    navigate('/', { replace: false });
  };

  const totalPages = Math.ceil(query.total / PER_PAGE);

  // Stale `?page=` deep link or a result set that shrank under the current
  // offset: the request comes back as an empty page with the exact count
  // intact — clamp to the last real page (or the first when nothing matches).
  useEffect(() => {
    if (query.status !== 'success') return;
    const lastIdx = Math.max(0, totalPages - 1);
    if (page > lastIdx) setPage(lastIdx);
  }, [query.status, totalPages, page]);

  const goPage = (idx: number) => {
    setPage(idx);
    // UI_Behavior §4.12 L725 — auto scroll back to the top of the grid on
    // page change (smooth, same target as the hero search submit).
    document
      .getElementById('catalogue-grid')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const deals = query.deals;
  // An out-of-range page renders as skeleton (not the empty state) for the
  // single frame before the clamp effect re-fetches the last real page.
  const isClampPending =
    query.status === 'success' && deals.length === 0 && query.total > 0 && page > 0;
  const isLoading = query.status === 'loading' || query.status === 'idle' || isClampPending;
  const headerCount = stats.stats?.count ?? (isLoading ? 0 : deals.length);

  const headerAverageDiscount = stats.stats?.averageDiscountPct ?? null;
  const headerLastUpdated = stats.stats?.lastUpdatedAt ?? null;
  const headerUpdatedRelative = useMemo(() => {
    if (!headerLastUpdated) return null;
    return formatRelativeTime(headerLastUpdated, i18n.language, t);
  }, [headerLastUpdated, i18n.language, t]);

  const bedroomChips: ChipOption[] = useMemo(
    () =>
      BEDROOMS_OPTS.map((key) => ({
        value: key,
        label: t(`catalogue.filters.bedroomsOpts.${key as BedroomKey}`),
      })),
    [t],
  );

  // Location corpus mapped to the field's suggestion shape (value/type/count).
  // The server already orders it Building-first then by popularity.
  const locationCorpus: LocationSuggestion[] = useMemo(
    () =>
      corpusQuery.options.map((o) => ({
        value: o.value,
        type: o.kind,
        dealCount: o.dealCount,
      })),
    [corpusQuery.options],
  );

  const locationSuggestionsFor = useCallback(
    (query: string): LocationSuggestion[] => {
      const term = query.trim().toLowerCase();
      const matches =
        term.length === 0
          ? locationCorpus
          : locationCorpus.filter((s) => s.value.toLowerCase().includes(term));
      return matches.slice(0, LOCATION_SUGGESTIONS_CAP);
    },
    [locationCorpus],
  );

  // The committed location value is the active free-text term. A pick or a
  // free-text submit sets it; legacy `?zone_key=` inbound links keep filtering
  // via the key columns without populating the field (rare external case).
  const locationValue = filters.locationText;

  // A pick / free-text submit replaces the whole location selection (one field,
  // one active location) and clears any legacy inbound keys.
  const onLocationSelect = (s: LocationSuggestion) =>
    setFilters({
      ...filters,
      zoneKey: [],
      communityKey: [],
      buildingKey: [],
      locationText: s.value,
    });

  const onLocationSubmit = (text: string) =>
    setFilters({
      ...filters,
      zoneKey: [],
      communityKey: [],
      buildingKey: [],
      locationText: text.trim(),
    });

  const onLocationClear = () =>
    setFilters({
      ...filters,
      zoneKey: [],
      communityKey: [],
      buildingKey: [],
      locationText: '',
    });

  // Chips i18n pour le filtre propertyType (catalogue M3, ADR-027). Partagés
  // entre la search row du hero et la toolbar compacte sous le hero —
  // mémoïsés pour stabiliser l'identité du tableau d'options.
  const propertyTypeChips: ChipOption[] = useMemo(
    () =>
      PROPERTY_TYPES.map((pt) => ({
        value: pt,
        label: t(`catalogue.filters.propertyTypeOpts.${pt}`),
      })),
    [t],
  );

  const handleSetPropertyTypes = (next: string[]) => setFilters({ ...filters, propertyType: next });

  // Suggestions surfaced to SearchInput — combine `title` and `building`
  // values returned by the DB, dedupe (a deal sets both columns to the
  // same value when the listing reuses the building name as its title),
  // cap at 6 to keep the dropdown scannable.
  const liveSuggestions = useMemo(() => {
    if (autocomplete.status !== 'success') return undefined;
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of autocomplete.items) {
      for (const candidate of [item.title, item.building]) {
        if (candidate && !seen.has(candidate)) {
          seen.add(candidate);
          out.push(candidate);
          if (out.length >= 6) return out;
        }
      }
    }
    return out;
  }, [autocomplete.status, autocomplete.items]);

  return (
    <div className="public">
      <HeroCataloguePremium
        filters={filters}
        setFilters={setFilters}
        searchDraft={searchDraft}
        setSearchDraft={setSearchDraft}
        isStatsLoading={stats.status === 'loading' || stats.status === 'idle'}
        headerCount={headerCount}
        headerAverageDiscount={headerAverageDiscount}
        headerUpdatedRelative={headerUpdatedRelative}
        locationValue={locationValue}
        locationSuggestionsFor={locationSuggestionsFor}
        onLocationSelect={onLocationSelect}
        onLocationSubmit={onLocationSubmit}
        onLocationClear={onLocationClear}
        bedroomChips={bedroomChips}
        propertyTypeChips={propertyTypeChips}
        propertyTypes={filters.propertyType}
        setPropertyTypes={handleSetPropertyTypes}
        liveSuggestions={liveSuggestions}
        onSubmitScrollTo={() =>
          document
            .getElementById('catalogue-grid')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      />

      {/* ADR-020 v3 — Toolbar compacte sous le hero : tabs + sort + reset.
       * Les autres filtres (zone, bedrooms, price, area, search, discount)
       * vivent dans le bloc search du hero — pas de duplication. */}
      <div className="catalogue-toolbar">
        <div className="catalogue-toolbar__inner">
          {(['all', 'ready', 'offPlan'] as CatalogueTypeFilter[]).map((tf) => (
            <button
              key={tf}
              type="button"
              onClick={() => setFilters({ ...filters, typeFilter: tf })}
              className={`catalogue-chip${filters.typeFilter === tf ? ' is-active' : ''}`}
            >
              {t(`catalogue.filters.${tf}`)}
            </button>
          ))}
          <span className="catalogue-toolbar__divider" aria-hidden="true" role="separator" />
          {PROPERTY_TYPES.map((pt) => (
            <button
              key={pt}
              type="button"
              onClick={() => togglePropertyType(pt)}
              className={`catalogue-chip${filters.propertyType.includes(pt) ? ' is-active' : ''}`}
              aria-pressed={filters.propertyType.includes(pt)}
              aria-label={t('catalogue.filters.propertyTypeAria')}
            >
              {t(`catalogue.filters.propertyTypeOpts.${pt}`)}
            </button>
          ))}
          <span className="catalogue-toolbar__spacer" />
          <span className="catalogue-toolbar__sort-label">{t('catalogue.sort.label')}</span>
          <select
            className="catalogue-toolbar__sort"
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as Sort })}
            aria-label={t('catalogue.sort.label')}
          >
            <option value="discount">{t('catalogue.sort.discount')}</option>
            <option value="priceAsc">{t('catalogue.sort.priceAsc')}</option>
            <option value="priceDesc">{t('catalogue.sort.priceDesc')}</option>
            <option value="dateDesc">{t('catalogue.sort.dateDesc')}</option>
          </select>
          <button type="button" className="catalogue-toolbar__reset" onClick={reset}>
            {t('catalogue.filters.reset')}
          </button>
        </div>
      </div>

      <section className="catalogue-grid-section" id="catalogue-grid">
        {isLoading ? (
          <div className="catalogue-grid">
            {Array.from({ length: PER_PAGE }, (_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div className="catalogue-empty">
            <h3 className="catalogue-empty__title">{t('catalogue.empty.title')}</h3>
            <p className="catalogue-empty__lead">{t('catalogue.empty.lead')}</p>
            <div className="public__cta-row public__cta-row--center">
              <button type="button" className="btn btn--primary" onClick={reset}>
                {t('catalogue.empty.reset')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="catalogue-grid">
              {deals.map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  showWhatsAppCta
                  whatsappPhone={resolveWhatsAppPhone(deal.advisorId)}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <nav
                className="catalogue-pagination"
                aria-label={t('catalogue.pagination.ariaLabel')}
              >
                <button
                  type="button"
                  className="catalogue-page-btn"
                  disabled={page === 0}
                  onClick={() => goPage(page - 1)}
                >
                  {t('catalogue.pagination.prev')}
                </button>
                {buildPaginationItems(page, totalPages).map((item, i) =>
                  item === 'ellipsis' ? (
                    <span key={`e-${i}`} className="catalogue-page-ellipsis" aria-hidden="true">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      className={`catalogue-page-num${item === page ? ' is-active' : ''}`}
                      onClick={() => goPage(item)}
                      aria-current={item === page ? 'page' : undefined}
                      aria-label={t('catalogue.pagination.goToPage', { page: item + 1 })}
                    >
                      {item + 1}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  className="catalogue-page-btn"
                  disabled={page >= totalPages - 1}
                  onClick={() => goPage(page + 1)}
                >
                  {t('catalogue.pagination.next')}
                </button>
                <span className="catalogue-pagination__count">
                  {t('catalogue.pagination.pageOfTotal', { page: page + 1, total: totalPages })}
                </span>
              </nav>
            )}
          </>
        )}
      </section>
    </div>
  );
}
